import Head from 'next/head';

import Footer from '@/components/common/Footer';
import Certificate from '@/components/v2/GraduatePage/Certificate';
import CertificateForDownload from '@/components/v2/GraduatePage/CertificateForDownload';
import Project from '@/components/v2/GraduatePage/Project';
import Report from '@/components/v2/GraduatePage/Report';
import Share from '@/components/v2/GraduatePage/Share';
import { useGraduateInfo } from '@/hooks/react-query/v2/graduate';
import { env } from '@/lib/utils/env';
import { Body, Header } from '@/pages/v2/graduate/[applicantId]/components';
import Template from '@/pages/v2/graduate/[applicantId]/template';

export default function Graduate({ applicantId }: { applicantId: string }) {
  // SEO 메타 태그 content 값
  const robotsContent =
    env.NEXT_PUBLIC_ENV === 'PRODUCTION'
      ? 'index, follow'
      : 'noindex, nofollow';
  const { data } = useGraduateInfo(applicantId);
  const startDate = data?.roundInfo.startDate
    .toString()
    .split('T')[0]
    .replaceAll('-', '.');
  const endDate = data?.roundInfo.endDate
    .toString()
    .split('T')[0]
    .replaceAll('-', '.');
  const round = data?.roundInfo.name;

  return (
    <>
      <Head>
        <title>항해99 | 수료 축하 페이지</title>
        <meta name='author' content='스파르타코딩클럽' />
        <meta
          name='description'
          content='개발자 취업 후기들이 증명합니다. 코딩 부트캠프 항해99. 99일간 개발 공부에 몰입하고 IT 기업에서 환영 받는 주니어 개발자로 취업하세요.'
        />
        <meta
          name='keywords'
          content='코딩 부트캠프, 주니어 개발자, 웹개발, 코딩공부, 프로그래밍, 파이썬, 코딩학원, 코딩 스터디, 부트캠프, 개발자, 코딩, 개발자 취업, AWS, 웹서버, 서버, 클라이언트, 자바스크립트, 리액트, React, Spring, 스프링, 앱개발, React Native, 프론트엔드, 백엔드, 풀스택'
        />
        <meta name='robots' content={robotsContent} />
        <meta name='googlebot' content={robotsContent} />
        <meta
          name='naver-site-verification'
          content='1136f21cf1c34f7ea97ea47922288c23ec478634'
        />
        <meta name='yeti' content={robotsContent} />
        <meta
          name='viewport'
          content='user-scalable=no,width=device-width,initial-scale=1,shrink-to-fit=no'
        />

        <meta
          property='og:url'
          content={`${process.env.NEXT_PUBLIC_PAGE_URL}/v2/graduate`}
        />
        <meta property='og:title' content='항해99 수료 축하 페이지' />
        <meta
          property='og:image'
          content={`${process.env.NEXT_PUBLIC_PAGE_URL}/images/graduate/graduate_og_image.png`}
        />
        <meta
          property='og:description'
          content='지난 99일 동안 보낸 여정을 살펴보세요!'
        />
        <meta property='og:site_name' content='항해99' />
        <meta property='og:type' content='website' />
        <meta property='og:locale' content='ko_KR' />
        <link rel='shortcut icon' href='/images/favicon/hanghae_favicon.ico' />
      </Head>
      {data && data.name && (
        <Template
          graduateName={data.name}
          header={<Header graduateName={data.name} />}
          body={
            <Body
              report={
                <Report
                  studyTime={data.studyTime}
                  teamMates={data.teamMatesCnt}
                  speciality={data.speciality}
                />
              }
              project={
                <Project graduateName={data.name} project={data.project} />
              }
              certificate={
                <Certificate
                  graduateName={data.name}
                  certificate={data.roundInfo}
                  applicantId={applicantId}
                  round={round}
                />
              }
              downloadCertificate={
                <CertificateForDownload
                  graduateName={data.name}
                  startDate={startDate}
                  endDate={endDate}
                  round={round}
                />
              }
              share={<Share />}
            />
          }
          footer={<Footer />}
        />
      )}
    </>
  );
}

Graduate.getInitialProps = async ({
  query,
}: {
  query: { applicantId: string };
}) => {
  const { applicantId } = query;
  return { applicantId };
};
